import { authenticatedFetch } from '@/utils/authenticatedFetch';
import { useToast } from '@/hooks/useToast';
import { Button, LuxModal, LuxModalBody, LuxModalFooter, LuxModalHeader } from '@/components/ui';
import ConfirmDeleteModal from '@components/business/ConfirmDeleteModal';
import {
  ArrowPathIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  PencilSquareIcon,
  PrinterIcon,
  ShoppingBagIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ToastContainer } from '@/components/ui/Toast';

type SyncResultSuccess = {
  found: number;
  imported: number;
  alreadyImported: number;
  skipped: number;
};
type SyncResultError = { error: string };
type SyncResult = SyncResultSuccess | SyncResultError | null;

type ExtractionStatus = 'FULL_MATCH' | 'PARTIAL_MATCH' | 'NEEDS_REVIEW';
type StatusFilter = 'ALL' | ExtractionStatus;

interface PurchaseInvoiceRow {
  id: string;
  supplier_name: string;
  invoice_number: string;
  invoice_date: string;
  file_path: string;
  gmail_message_id: string;
  extraction_status: ExtractionStatus;
  total_ttc: number | null;
  document_type: 'facture' | 'note_de_credit';
  created_at: string;
}

const STATUS_STYLE: Record<ExtractionStatus, string> = {
  FULL_MATCH: 'bg-green-100 text-green-800',
  PARTIAL_MATCH: 'bg-amber-100 text-amber-800',
  NEEDS_REVIEW: 'bg-red-100 text-red-800',
};

const STATUS_LABEL: Record<ExtractionStatus, string> = {
  FULL_MATCH: 'COMPLET',
  PARTIAL_MATCH: 'Partiel',
  NEEDS_REVIEW: 'REVISER',
};

// ─── Constantes datatable ─────────────────────────────────────────────────────

const ROW_HEIGHT = 33;
const MIN_ROWS = 5;
const CARD_CHROME = 34;
const NB_COLONNES = 7;

const formatEUR = (amount: number | null | undefined): string => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(amount);
};

const paginationBtnClass =
  'inline-flex items-center gap-1 rounded-token-lg border border-default bg-surface px-2 py-1 text-sm hover:bg-token-neutral-bg-hover disabled:opacity-40 disabled:pointer-events-none transition-colors';

function formatDateFR(isoDate: string | null | undefined): string {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('T')[0].split('-');
  return d && m && y ? `${d}/${m}/${y}` : isoDate;
}

// ─── Fetch function (exportée pour le prefetch) ─────────────────────────────

export async function fetchPurchaseInvoices(
  page: number,
  perPage: number,
  search: string,
  status: StatusFilter
): Promise<{ data: PurchaseInvoiceRow[]; total: number }> {
  const params = new URLSearchParams();
  if (search) params.set('supplier', search);
  if (status !== 'ALL') params.set('status', status);
  params.set('page', String(page));
  params.set('per_page', String(perPage));
  const res = await authenticatedFetch(`/api/purchase-invoices?${params.toString()}`);
  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Erreur chargement');
  return { data: json.data || [], total: typeof json.total === 'number' ? json.total : 0 };
}

// ─── Composant principal ───────────────────────────────────────────────────────

const AchatsPage: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  void setError; // suppress unused warning
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  const [editRow, setEditRow] = useState<PurchaseInvoiceRow | null>(null);
  const [editSupplierName, setEditSupplierName] = useState('');
  const [editExtractionStatus, setEditExtractionStatus] =
    useState<ExtractionStatus>('NEEDS_REVIEW');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const [viewRow, setViewRow] = useState<PurchaseInvoiceRow | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<PurchaseInvoiceRow | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const [syncLoading, setSyncLoading] = useState(false);
  const [syncElapsed, setSyncElapsed] = useState(0);
  const [syncResult, setSyncResult] = useState<SyncResult>(null);
  const [backfillLoading, setBackfillLoading] = useState(false);

  // Refs datatable
  const rootRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  // ── ResizeObserver ──────────────────────────────────────────────────────────

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const compute = () => {
      const totalHeight = el.clientHeight;
      const headerH = headerRef.current?.offsetHeight ?? 0;
      const searchH = searchRef.current?.offsetHeight ?? 0;
      const theadH = theadRef.current?.offsetHeight ?? 0;
      const paginationH = paginationRef.current?.offsetHeight ?? 0;

      const available = totalHeight - CARD_CHROME - headerH - searchH - theadH - paginationH;
      const r = Math.max(MIN_ROWS, Math.floor(available / ROW_HEIGHT) - 1);
      setRowsPerPage(r);
    };

    const observer = new ResizeObserver(() => compute());
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── Sync timer ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!syncLoading) return;
    setSyncElapsed(0);
    const interval = setInterval(() => setSyncElapsed(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [syncLoading]);

  // ── Debounce & reset ────────────────────────────────────────────────────────

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, rowsPerPage]);

  // ── Fetch via React Query ────────────────────────────────────────────────────

  const { data: queryData, refetch: fetchList } = useQuery({
    queryKey: ['achats', page, rowsPerPage, debouncedSearch, statusFilter],
    queryFn: () => fetchPurchaseInvoices(page, rowsPerPage, debouncedSearch, statusFilter),
    enabled: rowsPerPage > 0,
    staleTime: 30_000,
    placeholderData: (previousData: any) => {
      if (previousData) return previousData;
      const cached = queryClient.getQueriesData({ queryKey: ['achats'], exact: false });
      if (cached.length > 0) return (cached[0][1] as any) ?? undefined;
      return undefined;
    },
  });

  const list = queryData?.data || [];
  const total = queryData?.total || 0;

  // ── Computed ────────────────────────────────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const displayedCount = list.length;
  const emptyRowCount = Math.max(0, rowsPerPage - displayedCount);

  const getDownloadUrl = (id: string) => `/api/purchase-invoices/${id}/download`;

  // ── Edit ────────────────────────────────────────────────────────────────────

  const openEditModal = (row: PurchaseInvoiceRow) => {
    setEditRow(row);
    setEditSupplierName(row.supplier_name || '');
    setEditExtractionStatus(row.extraction_status);
    setEditError(null);
  };

  const closeEditModal = () => {
    if (!editLoading) {
      setEditRow(null);
      setEditSupplierName('');
      setEditError(null);
    }
  };

  const handleEditSubmit = async () => {
    if (!editRow) return;
    const trimmedName = editSupplierName.trim();
    const statusChanged = editExtractionStatus !== editRow.extraction_status;
    const nameChanged = trimmedName !== editRow.supplier_name;
    if (!statusChanged && !nameChanged) return;
    setEditLoading(true);
    setEditError(null);
    try {
      let res: Response;
      if (statusChanged && !nameChanged) {
        res = await authenticatedFetch(`/api/purchase-invoices/${editRow.id}/update-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ extraction_status: editExtractionStatus }),
        });
      } else {
        const body: { supplier_name?: string; extraction_status: ExtractionStatus } = {
          extraction_status: editExtractionStatus,
        };
        if (nameChanged && trimmedName) body.supplier_name = trimmedName;
        res = await authenticatedFetch(`/api/purchase-invoices/${editRow.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }
      const json = await res.json();
      if (!json.success) {
        setEditError(json.error || 'Erreur lors du renommage');
        return;
      }
      closeEditModal();
      await fetchList();
      toast.success('Fournisseur mis à jour');
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Erreur réseau');
    } finally {
      setEditLoading(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────

  const openDeleteModal = (row: PurchaseInvoiceRow) => {
    setRowToDelete(row);
    setDeleteError(null);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (!deleteLoading) {
      setDeleteModalOpen(false);
      setRowToDelete(null);
      setDeleteError(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const res = await authenticatedFetch(`/api/purchase-invoices/${rowToDelete.id}`, {
        method: 'DELETE',
      });
      const json = await res.json();
      if (!json.success) {
        setDeleteError(json.error || 'Erreur lors de la suppression');
        return;
      }
      closeDeleteModal();
      await fetchList();
      toast.success('Facture supprimée');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Erreur réseau');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Sync Gmail ──────────────────────────────────────────────────────────────

  const handleSync = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const res = await authenticatedFetch('/api/purchase-invoices/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const text = await res.text();
      let json: { success?: boolean; data?: SyncResultSuccess; error?: string } = {};
      try {
        json = JSON.parse(text);
      } catch {
        setSyncResult({ error: res.status === 500 ? 'Erreur serveur.' : text.slice(0, 200) });
        return;
      }
      if (!json.success) {
        setSyncResult({ error: json.error || 'Erreur sync' });
        return;
      }
      setSyncResult(json.data ?? null);
      await fetchList();
    } catch (err) {
      setSyncResult({ error: err instanceof Error ? err.message : 'Erreur réseau' });
    } finally {
      setSyncLoading(false);
    }
  };

  const closeSyncModal = () => {
    if (!syncLoading) {
      setSyncResult(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div ref={rootRef} className="absolute inset-0 flex flex-col overflow-hidden p-4">
      {/* Card */}
      <div className="flex-1 min-h-0 flex flex-col bg-surface rounded-xl shadow-sm border border-default overflow-hidden">
        {/* Header */}
        <div
          ref={headerRef}
          className="flex-shrink-0 px-6 py-3 border-b border-default flex items-center justify-between bg-blue-50"
        >
          <div>
            <h1 className="text-base font-semibold text-ink flex items-center gap-2">
              <ShoppingBagIcon className="w-5 h-5" />
              Achats
            </h1>
            <p className="text-xs text-muted mt-0.5">
              {total} facture{total > 1 ? 's' : ''} fournisseurs
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fetchList()}
              title="Actualiser"
              aria-label="Actualiser"
              className="p-1 rounded hover:bg-blue-100 transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4 text-ink" />
            </button>
            <Button
              variant="secondary"
              size="sm"
              icon={
                backfillLoading ? (
                  <span className="inline-block w-4 h-4 animate-spin rounded-full border-2 border-purple-400 border-t-transparent" />
                ) : (
                  <ArrowPathIcon className="h-4 w-4" />
                )
              }
              onClick={async () => {
                setBackfillLoading(true);
                try {
                  const supplier = debouncedSearch || null;
                  const res = await authenticatedFetch('/api/purchase-invoices/backfill', {
                    method: 'POST',
                    body: JSON.stringify({
                      useClaude: true,
                      supplierName: supplier,
                      forceOverwrite: true,
                    }),
                  });
                  const data = await res.json();
                  toast.success(`${data.updated} facture(s) corrigée(s) via Claude`);
                  fetchList();
                } catch (err) {
                  toast.error(err instanceof Error ? err.message : 'Erreur');
                } finally {
                  setBackfillLoading(false);
                }
              }}
              disabled={backfillLoading}
            >
              {backfillLoading
                ? 'Analyse...'
                : `Recalculer${debouncedSearch ? ` (${debouncedSearch})` : ''}`}
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<ArrowPathIcon className="h-4 w-4" />}
              onClick={handleSync}
              disabled={syncLoading}
              loading={syncLoading}
            >
              Sync Gmail
            </Button>
          </div>
        </div>

        {/* Recherche + filtres statut */}
        <div
          ref={searchRef}
          className="flex-shrink-0 px-6 py-2 border-b border-default flex flex-wrap items-center gap-3"
        >
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Rechercher par fournisseur…"
            className="flex-1 min-w-[200px] rounded-token-lg border border-default bg-panel px-3 py-1.5 text-sm text-ink
                       placeholder:text-muted focus:border-blue-500 focus:outline-none
                       focus:ring-2 focus:ring-token-focus focus:ring-offset-token-focus transition-colors"
          />
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                setStatusFilter('ALL');
                setPage(1);
              }}
              className={`rounded px-2 py-1 text-xs font-medium transition ${
                statusFilter === 'ALL'
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Tous
            </button>
            <button
              type="button"
              onClick={() => {
                setStatusFilter('FULL_MATCH');
                setPage(1);
              }}
              className={`rounded px-2 py-1 text-xs font-medium transition ${
                statusFilter === 'FULL_MATCH'
                  ? 'bg-green-600 text-white'
                  : 'bg-green-100 text-green-800 hover:bg-green-200'
              }`}
            >
              COMPLET
            </button>
            <button
              type="button"
              onClick={() => {
                setStatusFilter('NEEDS_REVIEW');
                setPage(1);
              }}
              className={`rounded px-2 py-1 text-xs font-medium transition ${
                statusFilter === 'NEEDS_REVIEW'
                  ? 'bg-red-600 text-white'
                  : 'bg-red-100 text-red-800 hover:bg-red-200'
              }`}
            >
              REVISER
            </button>
          </div>
        </div>

        {/* Erreur */}
        {error && (
          <div className="flex-shrink-0 mx-6 mt-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        {/* Table area */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <table className="w-full text-sm">
            <colgroup>
              <col /> {/* Fournisseur - auto */}
              <col className="hidden md:table-column w-[14%]" /> {/* N° facture */}
              <col className="w-[10%]" /> {/* Date facture */}
              <col className="w-[10%]" /> {/* Montant TTC */}
              <col className="w-[9%]" /> {/* Statut */}
              <col className="hidden lg:table-column w-[10%]" /> {/* Date import */}
              <col className="w-[10%]" /> {/* Actions */}
            </colgroup>

            <thead ref={theadRef} className="bg-surface border-b border-default">
              <tr>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-muted uppercase tracking-wide border-r border-gray-100">
                  Fournisseur
                </th>
                <th className="hidden md:table-cell px-3 py-1.5 text-left text-xs font-semibold text-muted uppercase tracking-wide border-r border-gray-100">
                  N° facture
                </th>
                <th className="px-3 py-1.5 text-left text-xs font-semibold text-muted uppercase tracking-wide border-r border-gray-100">
                  Date facture
                </th>
                <th className="px-3 py-1.5 text-right text-xs font-semibold text-muted uppercase tracking-wide border-r border-gray-100">
                  Montant TTC
                </th>
                <th className="px-3 py-1.5 text-center text-xs font-semibold text-muted uppercase tracking-wide border-r border-gray-100">
                  Statut
                </th>
                <th className="hidden lg:table-cell px-3 py-1.5 text-left text-xs font-semibold text-muted uppercase tracking-wide border-r border-gray-100">
                  Date import
                </th>
                <th className="px-3 py-1.5 text-center text-xs font-semibold text-muted uppercase tracking-wide">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {list.length === 0 ? (
                <tr>
                  <td colSpan={NB_COLONNES} className="px-4 py-8 text-center text-sm text-muted">
                    Aucune facture trouvée.
                  </td>
                </tr>
              ) : (
                list.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`border-b border-gray-100 transition-colors hover:bg-blue-50
                      ${index % 2 === 0 ? '' : 'bg-gray-50/60'}`}
                  >
                    <td className="px-3 py-1 text-sm border-r border-gray-100 overflow-hidden">
                      <span className="block truncate text-gray-900" title={row.supplier_name}>
                        {row.supplier_name}
                      </span>
                    </td>
                    <td className="hidden md:table-cell px-3 py-1 text-sm border-r border-gray-100 whitespace-nowrap text-gray-700">
                      {row.invoice_number}
                    </td>
                    <td className="px-3 py-1 text-sm border-r border-gray-100 whitespace-nowrap text-gray-600">
                      {formatDateFR(row.invoice_date)}
                    </td>
                    <td
                      className={`px-3 py-1 text-sm border-r border-gray-100 whitespace-nowrap text-right font-medium ${row.document_type === 'note_de_credit' ? 'text-red-600' : 'text-ink'}`}
                    >
                      {row.document_type === 'note_de_credit' ? 'NC ' : ''}
                      {formatEUR(row.total_ttc)}
                    </td>
                    <td className="px-3 py-1 text-sm border-r border-gray-100 text-center">
                      <span
                        className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[row.extraction_status]}`}
                      >
                        {STATUS_LABEL[row.extraction_status]}
                      </span>
                    </td>
                    <td className="hidden lg:table-cell px-3 py-1 text-sm border-r border-gray-100 whitespace-nowrap text-gray-500">
                      {formatDateFR(row.created_at)}
                    </td>
                    <td className="px-3 py-1 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => setViewRow(row)}
                          title="Voir le PDF"
                          aria-label="Voir le PDF"
                          className="p-1 rounded hover:bg-blue-100 transition-colors"
                        >
                          <EyeIcon className="w-4 h-4 text-ink" />
                        </button>
                        <a
                          href={getDownloadUrl(row.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Télécharger / Imprimer PDF"
                          aria-label="Télécharger / Imprimer PDF"
                          className="p-1 rounded hover:bg-blue-100 transition-colors"
                        >
                          <PrinterIcon className="w-4 h-4 text-ink" />
                        </a>
                        <button
                          type="button"
                          onClick={() => openEditModal(row)}
                          title="Modifier fournisseur et statut"
                          aria-label="Modifier fournisseur et statut"
                          className="p-1 rounded hover:bg-blue-100 transition-colors"
                        >
                          <PencilSquareIcon className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openDeleteModal(row)}
                          title="Supprimer"
                          aria-label="Supprimer"
                          className="p-1 rounded hover:bg-red-100 transition-colors"
                        >
                          <TrashIcon className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}

              {/* Lignes vides de padding */}
              {list.length > 0 &&
                Array.from({ length: emptyRowCount }).map((_, i) => (
                  <tr
                    key={`empty-${i}`}
                    className={`border-b border-gray-100 ${(list.length + i) % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}`}
                  >
                    <td colSpan={NB_COLONNES} className="px-3 py-1">
                      &nbsp;
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div
          ref={paginationRef}
          className="flex-shrink-0 px-6 py-2 border-t border-default flex items-center justify-between bg-blue-50"
        >
          <p className="text-sm text-muted">
            Page {page} / {totalPages} · {total} facture{total > 1 ? 's' : ''} · {rowsPerPage}{' '}
            lignes/page
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1}
              className={paginationBtnClass}
            >
              <ChevronDoubleLeftIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Premier</span>
            </button>
            <button
              type="button"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className={paginationBtnClass}
            >
              <ChevronLeftIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Préc.</span>
            </button>
            <button
              type="button"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className={paginationBtnClass}
            >
              <span className="hidden sm:inline">Suiv.</span>
              <ChevronRightIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className={paginationBtnClass}
            >
              <span className="hidden sm:inline">Dernier</span>
              <ChevronDoubleRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal Edit (renommer fournisseur) */}
      <LuxModal isOpen={!!editRow} onClose={closeEditModal} size="md">
        <LuxModalHeader onClose={closeEditModal} showCloseButton>
          Modifier le fournisseur
        </LuxModalHeader>
        <LuxModalBody>
          {editRow && (
            <>
              <p className="mb-2 text-sm text-gray-600">N° facture : {editRow.invoice_number}</p>
              <label className="block text-sm font-medium text-gray-700">Nom du fournisseur</label>
              <input
                type="text"
                value={editSupplierName}
                onChange={e => setEditSupplierName(e.target.value)}
                className="mt-1 block w-full rounded border border-default px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-token-focus"
                placeholder="ex. Dats"
                disabled={editLoading}
              />
              <div className="mt-4 flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-100 px-4 py-3">
                <span className="text-sm font-medium text-gray-700">Marquer comme COMPLET</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={editExtractionStatus === 'FULL_MATCH'}
                  disabled={editLoading}
                  onClick={() =>
                    setEditExtractionStatus(
                      editExtractionStatus === 'FULL_MATCH' ? 'NEEDS_REVIEW' : 'FULL_MATCH'
                    )
                  }
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    editExtractionStatus === 'FULL_MATCH' ? 'bg-green-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                      editExtractionStatus === 'FULL_MATCH' ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
              <p className="mt-1.5 text-xs text-gray-500">
                {editExtractionStatus === 'FULL_MATCH' ? 'Statut : COMPLET' : 'Statut : REVISER'}
              </p>
              {editError && <p className="mt-2 text-sm text-red-600">{editError}</p>}
            </>
          )}
        </LuxModalBody>
        <LuxModalFooter>
          <Button variant="secondary" onClick={closeEditModal} disabled={editLoading}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={handleEditSubmit}
            disabled={
              editLoading ||
              (editSupplierName.trim() === editRow?.supplier_name &&
                editExtractionStatus === editRow?.extraction_status)
            }
          >
            {editLoading ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </LuxModalFooter>
      </LuxModal>

      {/* Modal View PDF */}
      <LuxModal
        isOpen={!!viewRow}
        onClose={() => setViewRow(null)}
        size="lg"
        className="!max-w-[60%] w-full"
      >
        <LuxModalBody className="p-0 flex flex-col overflow-hidden relative min-h-[90vh]">
          <button
            type="button"
            onClick={() => setViewRow(null)}
            className="absolute top-2 right-2 z-10 rounded-full p-1.5 bg-white/90 shadow hover:bg-gray-100 text-gray-600"
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          {viewRow && (
            <div className="flex-1 min-h-0 overflow-auto flex items-center justify-center bg-gray-100 p-4">
              <div className="w-[135%] max-w-full">
                <iframe
                  title="Aperçu PDF"
                  src={getDownloadUrl(viewRow.id)}
                  className="w-full h-[80vh] min-h-[60vh] border-0 rounded-b"
                />
              </div>
            </div>
          )}
        </LuxModalBody>
      </LuxModal>

      {/* Modal Delete */}
      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Supprimer la facture"
        message="Cette facture sera supprimée définitivement (base de données et fichier PDF)."
        itemName={
          rowToDelete ? `${rowToDelete.supplier_name} – ${rowToDelete.invoice_number}` : undefined
        }
        isDeleting={deleteLoading}
        errorMessage={deleteError}
      />

      {/* Modal Synchronisation Gmail */}
      <LuxModal
        isOpen={syncLoading || syncResult !== null}
        onClose={closeSyncModal}
        size="sm"
        closeOnBackdrop={!syncLoading}
        closeOnEscape={!syncLoading}
      >
        <LuxModalHeader
          onClose={syncLoading ? undefined : closeSyncModal}
          showCloseButton={!syncLoading}
        >
          Synchronisation Gmail
        </LuxModalHeader>
        <LuxModalBody className="text-center">
          {syncLoading && (
            <>
              <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                <div
                  className="h-full rounded-full bg-indigo-600 transition-all duration-500 ease-out"
                  style={{
                    width: `${Math.min(90, (syncElapsed / 120) * 90)}%`,
                  }}
                />
              </div>
              <p className="mt-3 text-sm text-gray-600">
                {syncElapsed} s - Traitement des emails en cours… Peut prendre 1 à 2 minutes.
              </p>
            </>
          )}
          {!syncLoading && syncResult !== null && (
            <>
              {'error' in syncResult ? (
                <p className="text-sm text-red-600">{syncResult.error}</p>
              ) : (
                <p className="text-sm text-gray-700">
                  {syncResult.imported} importée(s), {syncResult.alreadyImported} déjà en base,{' '}
                  {syncResult.skipped} ignorée(s).
                </p>
              )}
            </>
          )}
        </LuxModalBody>
        {!syncLoading && syncResult !== null && (
          <LuxModalFooter>
            <Button variant="primary" onClick={closeSyncModal}>
              Fermer
            </Button>
          </LuxModalFooter>
        )}
      </LuxModal>

      <ToastContainer toasts={toast.toasts} onClose={toast.removeToast} />
    </div>
  );
};

export default AchatsPage;
